import type { ReactNode } from 'react';
import { z } from 'zod';

import { getIsYouTubeUrl } from '@/components/Video/VideoUtils.ts';

import {
    CmsCallout,
    CmsCloudflareStreamEmbed,
    CmsInstagramEmbed,
    CmsXPostEmbed,
    CmsYouTubeEmbed,
} from './cmsMarkdownEmbeds';
import {
    isAllowedInstagramPostUrl,
    isAllowedYouTubeEmbedUrl,
    isAllowedXPostUrl,
    isCloudflareStreamUid,
    parseXPostStatusId,
} from './cmsSafeMedia.ts';

const youtubeSchema = z.object({
    url: z.string().url(),
});

/** CMS authors may set `orientation="portrait"` for 9:16 Cloudflare Stream (matches Cloudflare’s ~177.78% embed box). */
const cloudflareStreamSchema = z.object({
    uid: z.string(),
    orientation: z.enum(['landscape', 'portrait']).optional(),
});

const calloutSchema = z.object({
    text: z.string().min(1),
    tone: z.enum(['info', 'warning']).optional(),
});

const socialUrlSchema = z.object({
    url: z.string().url(),
});

export type CmsMarkdownComponentType
    = | 'Video'
        | 'CloudflareStream'
        | 'Callout'
        | 'Instagram'
        | 'XPost';

export function renderCmsMarkdownComponent(type: string, props: Record<string, string>): ReactNode {
    switch (type as CmsMarkdownComponentType) {
        case 'Video': {
            const parsed = youtubeSchema.safeParse({ url: props.url ?? '' });
            if (
                !parsed.success
                || !isAllowedYouTubeEmbedUrl(parsed.data.url)
                || !getIsYouTubeUrl(parsed.data.url)
            ) {
                return null;
            }

            return <CmsYouTubeEmbed url={parsed.data.url} />;
        }
        case 'CloudflareStream': {
            const raw = props.orientation?.trim().toLowerCase() ?? '';
            let orientation: 'landscape' | 'portrait' | undefined;
            if (raw === 'portrait') {
                orientation = 'portrait';
            }
            else if (raw === 'landscape') {
                orientation = 'landscape';
            }
            const parsed = cloudflareStreamSchema.safeParse({ uid: props.uid ?? '', orientation });
            if (!parsed.success || !isCloudflareStreamUid(parsed.data.uid)) {
                return null;
            }

            return (
                <CmsCloudflareStreamEmbed
                    uid={parsed.data.uid}
                    orientation={parsed.data.orientation ?? 'landscape'}
                />
            );
        }
        case 'Callout': {
            const tone = props.tone === 'warning' || props.tone === 'info' ? props.tone : undefined;
            const parsed = calloutSchema.safeParse({ text: props.text ?? '', tone });
            if (!parsed.success) {
                return null;
            }

            return <CmsCallout text={parsed.data.text} tone={parsed.data.tone ?? 'info'} />;
        }
        case 'Instagram': {
            const parsed = socialUrlSchema.safeParse({ url: props.url ?? '' });
            if (!parsed.success || !isAllowedInstagramPostUrl(parsed.data.url)) {
                return null;
            }

            return <CmsInstagramEmbed url={parsed.data.url} />;
        }
        case 'XPost': {
            const parsed = socialUrlSchema.safeParse({ url: props.url ?? '' });
            if (!parsed.success || !isAllowedXPostUrl(parsed.data.url)) {
                return null;
            }

            const statusId = parseXPostStatusId(parsed.data.url);
            if (!statusId) {
                return null;
            }

            return <CmsXPostEmbed statusId={statusId} />;
        }
        default:
            return null;
    }
}
